import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BDFlag from "../../assets/icons/BDFlag";
import DateIcon from "../../assets/icons/DateIcon";
import SelectDropdownLgIcon from "../../assets/icons/SelectDropdownLgIcon";
import {
  dateFormat,
  dbDateFormat,
  genderList,
  mailRegex,
} from "../../constants/constants";
import { useGetDepartmentsQuery } from "../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../features/designation/designationApi";
import { useAddEmployeeMutation } from "../../features/employee/employeeApi";
import { useSingleEmployeeProrateRunMutation } from "../../features/leaveProrate/leaveProrateApi";
import useError from "../../hooks/useError";
import useModal from "../../hooks/useModal";
import { toast } from "../../lib/toast";
import { ModalComponentProps } from "../../types";
import {
  formatPhoneNumber,
  isValidTLD,
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
} from "../../utils/utils";
import ConfirmModal from "../ui/ConfirmModal";
import CustomModal from "../ui/CustomModal";

type Props = {
  showGrossAmount?: boolean;
} & ModalComponentProps;

export default function AddEmployeeModal({
  handleClose,
  showGrossAmount,
  ...rest
}: Props) {
  const [form] = Form.useForm();
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  );
  const {
    open,
    handleClose: handleAutoClose,
    // handleOpen,
  } = useModal<string, "AUTO">();
  const {
    data: departmentData,
    isError: isDepartmentError,
    error: departmentError,
  } = useGetDepartmentsQuery({
    params: { limit: 0, skip: 0, is_published: 1 },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(departmentId
          ? { business_department_id: departmentId?.toString() || "" }
          : {}),
      },
    },
    {
      skip: !departmentId,
    }
  );

  const [addEmployee, { isLoading, isError, isSuccess, error }] =
    useAddEmployeeMutation();
  const [
    singleEmployeeProrateRun,
    {
      isLoading: isRunLoading,
      isError: isRunError,
      error: runError,
      isSuccess: isRunSuccess,
    },
  ] = useSingleEmployeeProrateRunMutation();
  // error toast message
  useError(isDepartmentError, departmentError, "Get department fail");
  useError(isDesignationError, designationError, "Get designation fail");
  useError(isError, error, "Employee added fail");
  useError(isRunError, runError, "Auto prorate run fail");

  const onFinish = (values: any) => {
    const { join_date, department_id, mobile, ...rest } = values;
    const newData = {
      ...rest,
      mobile: formatPhoneNumber(mobile),
      department: departmentData?.data?.departments?.find(
        (department) => department?.id === department_id
      )?.name,
      department_id,
      join_date: join_date ? join_date?.format(dbDateFormat) : undefined,
    };

    return addEmployee(newData);
  };

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "Employee added successfully");
      // handleOpen({ type: "AUTO" });
      return handleClose();
    }
  }, [handleClose, isSuccess]);

  useEffect(() => {
    if (isRunSuccess) {
      toast("success", "Auto prorate run successfully");
      handleAutoClose();
      return handleClose();
    }
  }, [handleAutoClose, handleClose, isRunSuccess]);

  return (
    <CustomModal
      title="Add new employee"
      {...rest}
      handleClose={handleClose}
      width={800}
      footer={null}
    >
      {open?.type === "AUTO" && (
        <ConfirmModal
          open={open?.open}
          handleOk={() => {
            singleEmployeeProrateRun({
              id: 1,
              body: {
                is_prorated: "yes",
                prorate_type: "profile_complete",
              },
            });
          }}
          handleClose={handleClose}
          okText="Confirm"
          confirmTitle="Auto prorate"
          questionText="Are you want to run auto prorate for this employee?"
          okLoading={isRunLoading}
        />
      )}
      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <div className="grid md:grid-cols-2 gap-x-4">
          <Form.Item
            name="name"
            label="Full Name"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please enter full name",
              },
            ]}
          >
            <Input size="large" placeholder="Enter employee name" />
          </Form.Item>
          <Form.Item
            name="gender"
            label="Gender"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please select a gender",
              },
            ]}
          >
            <Select
              placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectStringOption(genderList)}
              suffixIcon={<SelectDropdownLgIcon />}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            className="mb-4"
            validateFirst
            rules={[
              {
                required: true,
                message: "Please enter email",
              },
              {
                validator: async (_: any, value: string) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (!mailRegex.test(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid email")
                    );
                  }

                  if (!isValidTLD(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid TLD")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input size="large" placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="mobile"
            label="Contact Number"
            className="mb-4"
            initialValue="+880"
            rules={[
              {
                validator: async (_: any, value: string) => {
                  if (!value) {
                    return Promise.reject(
                      new Error("Please enter phone number")
                    );
                  }
                  // if (!value) {
                  //   return Promise.resolve();
                  // }

                  const phoneRegex = /^(\+88)?(01[3-9])[0-9]{8}$/;
                  if (!phoneRegex.test(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid Bangladeshi phone number")
                    );
                  }
                },
              },
            ]}
          >
            <Input prefix={<BDFlag />} size="large" placeholder="+8801" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="Department"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please select department",
              },
            ]}
          >
            <Select
              placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDropdownLgIcon />}
              onChange={(val) => {
                setDepartmentId(val);
                form.setFieldValue("business_role_id", undefined);
              }}
            />
          </Form.Item>
          <Form.Item
            name="business_role_id"
            label="Designation"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please select designation",
              },
            ]}
          >
            <Select
              placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOption(
                designationData?.data?.roles || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDropdownLgIcon />}
              disabled={!departmentId}
            />
          </Form.Item>
          {showGrossAmount ? (
            <Form.Item
              name="salary"
              className="mb-4"
              label="Gross Salary"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(
                        new Error("Enter valid gross salary")
                      );
                    }
                    if (value < 0) {
                      return Promise.reject(
                        new Error("Gross salary can't be negative")
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                size="large"
                placeholder="0"
                prefix="BDT"
              />
            </Form.Item>
          ) : null}
          <div className="mb-2.5">
            <Form.Item
              name="join_date"
              label="Joining Date"
              className="mb-1.5"
              rules={[
                {
                  required: true,
                  message: "Please select joining date",
                },
              ]}
            >
              <DatePicker
                format={dateFormat}
                suffixIcon={<DateIcon width={18} height={20} />}
                style={{ width: "100%" }}
                size="large"
              />
            </Form.Item>
            <small className="block text-[#5E718D] text-sm font-normal">
              Employee's Leave days will be auto prorated based on their joining
              date. If you want to change the settings,{" "}
              <Link
                to="/admin/settings/leave/others"
                className="text-accent hover:underline cursor-pointer"
              >
                click here
              </Link>
            </small>
          </div>
        </div>

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            loading={isLoading}
          >
            Add Employee
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}
